import {Component, ElementRef, Inject, OnInit, ViewChild} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';

import * as tinycolor from 'tinycolor2';
import {RenderService, SubPalette} from '../render.service';
import {ThemingService} from '../../app/theming.service';
import {debounceTime, distinctUntilChanged, filter, first, switchMap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {ThemeConfig} from '../../app/theme-config';
import {BLUE_THEME, DEFAULT_THEME, PWC_THEME, RED_THEME} from '../../app/default-theme';
import { AuthService } from 'app/services/auth.service';
import { PermissionType } from 'app/enums/permission.enum';
import { QuillEditorComponent } from 'ngx-quill';

import * as Quill from 'quill';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export const LDD_MODULES = [
  'main',
  'report-irregularity',
  'resources',
  'frequent-questions',
  'follow-up',
  'make-question',
];
export const LDD_GLOBAL_MODULES = [
  'main',
  'resources',
  'frequent-questions',
];

export interface ThemeBuilderData {
  initialData: Observable<ThemeConfig>;
  modules: typeof LDD_MODULES | typeof LDD_GLOBAL_MODULES;
  onThemeChange: (theme: ThemeConfig) => void;
  onCancel: () => void;
  onPersist: (theme: ThemeConfig) => void;
}

export interface Palette {
  primary: SubPalette;
  accent: SubPalette;
  warn: SubPalette;
  darkText: string;
  darkBackground: string;
  lightText: string;
  lightBackground: string;
}

@Component({
  selector: 'app-palette-picker',
  templateUrl: './palette-picker.component.html',
  styleUrls: ['./palette-picker.component.scss']
})
export class PalettePickerComponent implements OnInit {

  form: UntypedFormGroup;
  public harmony: UntypedFormControl;
  public themes: Partial<ThemeConfig>[] = [
    DEFAULT_THEME,
    BLUE_THEME,
    RED_THEME,
  ];
  public readonly CUSTOM_THEME_TOKEN = 'CuStOm';
 
  toolbarOptions = [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    ['blockquote', 'code-block'],
  
    [{ 'header': 1 }, { 'header': 2 }],               // custom button values
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
    [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent
    [{ 'direction': 'rtl' }],                         // text direction
  
    [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
  
    [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    [{ 'font': [] }],
    [{ 'align': [] }],
  
    ['clean']                                         // remove formatting button
  ];

  readonly MODULES: string[] = [];
  savePaletteBtn = false;

  @ViewChild('quill') quillComponent: QuillEditorComponent;
  @ViewChild('editor', {static: false})
  editorElementRef: ElementRef;
  editor: any;
  constructor(
    private themingService: ThemingService,
    @Inject(MAT_DIALOG_DATA)
    private dialogData: ThemeBuilderData,
    public _dialogRef: MatDialogRef<PalettePickerComponent>,
    private authService: AuthService,
    ) {
    this.savePaletteBtn = this.authService.userHasPermission(PermissionType.TenantEdit);
    this.MODULES = this.dialogData.modules;

    this.form = new UntypedFormGroup({
      themeName: new UntypedFormControl(''),
      mainColors: new UntypedFormGroup({
        primaryColor: new UntypedFormControl(''),
        accentColor: new UntypedFormControl(''),
        warnColor: new UntypedFormControl('')
      }),
      components: new UntypedFormGroup({
        toolbar: new UntypedFormGroup({
          backgroundColor: new UntypedFormControl(''),
          textColor: new UntypedFormControl(''),
        }),
        navbarSecondary: new UntypedFormGroup({
          backgroundColor: new UntypedFormControl(''),
          textColor: new UntypedFormControl(''),
        }),
        navbarPrimary: new UntypedFormGroup({
          backgroundColor: new UntypedFormControl(''),
          textColor: new UntypedFormControl(''),
        }),
      }),
      images: new UntypedFormGroup({
        errorBackground: new UntypedFormControl('/assets/images/backgrounds/ct-login-bg.jpg'),
        favIcon: new UntypedFormControl('favicon.ico'),
        menuLogo: new UntypedFormControl('/assets/images/logos/pwc_white.png'),
      }),
      lddModules: new UntypedFormGroup(
        // Produces correct form structure for each LDD module
        this.MODULES.reduce((acc, module) => {
          return {
            ...acc,
            [module]: new UntypedFormGroup({
              textColor: new UntypedFormControl(''),
              backgroundColor: new UntypedFormControl(''),
              backgroundImage: new UntypedFormControl(''),
              hideText: new UntypedFormControl(false),
            }),
          };
        }, {})
      ),
      footerText: new UntypedFormControl(''),
      darkTheme: new UntypedFormControl(),
      lightText: new UntypedFormControl('', []),
      lightBackground: new UntypedFormControl('', []),
      darkText: new UntypedFormControl('', []),
      darkBackground: new UntypedFormControl('', []),

    });

    // Inner controls
    this.harmony = new UntypedFormControl(false);


    // this.form.valueChanges
    //   .pipe(
    //     throttleTime(200)
    //   ).subscribe(x => {
    //     const errs = this.validateLegibility(this.form);
    //     this.form.setErrors(errs);
    //   });
  }

  isLegible(l1: string | tinycolor.Instance, l2: string | tinycolor.Instance, threshold = 4.5) {
    const rl1 = tinycolor(l1).getLuminance();
    const rl2 = tinycolor(l2).getLuminance();
    if (rl1 > rl2) {
      return (rl1 + .05) / (rl2 + .05) > threshold;
    } else {
      return (rl2 + .05) / (rl1 + .05) > threshold;
    }
  }

  validateLegibility(form: UntypedFormGroup) {
    const f = form.value;

    const lightText = f.lightText;
    const lightBackground = tinycolor(f.lightBackground);
    const darkText = f.darkText;
    const darkBackground = tinycolor(f.darkBackground);

    const pcol = tinycolor(f.primary.main);
    const acol = tinycolor(f.accent.main);
    const wcol = tinycolor(f.warn.main);

    let lightLegible = this.isLegible(lightText, lightBackground)
      && this.isLegible(lightText, lightBackground.darken(10))
      && this.isLegible(lightText, lightBackground.darken(20));
    // && this.isLegible(wcol, lightBackground)
    // && this.isLegible(wcol, lightBackground.darken(10));


    let darkLegible = this.isLegible(darkText, darkBackground)
      && this.isLegible(darkText, darkBackground.lighten(10))
      && this.isLegible(darkText, darkBackground.lighten(20));
    // && this.isLegible(wcol, darkBackground)
    // && this.isLegible(wcol, darkBackground.lighten(10));

    if (tinycolor(pcol).isDark()) {
      darkLegible = darkLegible && this.isLegible(darkText, pcol);
    } else {
      lightLegible = lightLegible && this.isLegible(lightText, pcol);
    }

    if (tinycolor(acol).isDark()) {
      darkLegible = darkLegible && this.isLegible(darkText, acol);
    } else {
      lightLegible = lightLegible && this.isLegible(lightText, acol);
    }

    const errors = {};
    if (!lightLegible) {
      errors['illegible-light'] = { valid: false };
    }
    if (!darkLegible) {
      errors['illegible-dark'] = { valid: false };
    }
    return Object.keys(errors) ? errors : null;
  }

  onChangeQuillSelection(e){
    // console.log(e);
    e.oldRange = e.range;
    e.editor.selection.savedRange = e.range;
    e.editor.selection.lastRange = e.range;
    e.editor.selection.cursor.selection.savedRange = e.range;
    e.editor.selection.cursor.selection.lastRange = e.range;
  }

  ngOnInit() {

    // this.form.patchValue({
    //   primary: { main: '#cc33ca' },
    //   accent: { main: '#797979' },
    //   warn: { main: '#ff0000' },
    //   lightText: '#000000',
    //   lightBackground: '#fafafa',
    //   darkText: '#ffffff',
    //   darkBackground: '#2c2c2c'
    // });

    this.dialogData.initialData.pipe(
      // first(x => !!x && x.themeName !== PWC_THEME.themeName),
    ).subscribe(value => {
      this.form.patchValue(value, {emitEvent: false});


      // Setear color de texto para componentes basado en el background
      Object.entries((this.form.get('components') as UntypedFormGroup).controls).forEach(([componentName, formGroup]) => {
        formGroup.get('backgroundColor').valueChanges.pipe(
          // debounceTime(500),
          distinctUntilChanged(),
        )
          .subscribe(color => {
            formGroup.get('textColor').setValue(tinycolor(color).isLight() ? '#000' : '#fff');
          });
      });

      // Setear color de texto para headers de los módulos basado en el background
      Object.entries((this.form.get('lddModules') as UntypedFormGroup).controls).forEach(([moduleName, formGroup]) => {
        formGroup.get('backgroundColor').valueChanges.pipe(
          // debounceTime(500),
          distinctUntilChanged(),
        )
          .subscribe(color => {
            formGroup.get('textColor').setValue(tinycolor(color).isLight() ? '#000' : '#fff');
          });
      });

      this.form.valueChanges
        .pipe(
          filter(theme => {
            if (theme.themeName !== this.CUSTOM_THEME_TOKEN) {
              this.form.get('themeName').setValue(this.CUSTOM_THEME_TOKEN);
              return false;
            }
            return true;
          }),
          debounceTime(500),
        )
        .subscribe(x => {
          // this.service.palette = x;
          // this.themingService.previewTheme(x);
          this.dialogData.onThemeChange(x);
      });
      //set text footer
      //this.editor?.setText(value.footerText);
      if(this.editor){
        this.editor.root.innerHTML = value.footerText;
      }

    });

    this.harmony
      .valueChanges
      .pipe(
        switchMap(harmony => {
          return harmony ? this.form.get('mainColors').get('primaryColor').valueChanges : of(null);
        }),
        filter(x => !!x),
      )
      .subscribe(color => {

        const material = RenderService.getPalette(color);

        // if (!this.unlocked.value) {
        this.form.get('components').get('toolbar').get('backgroundColor').patchValue(material['100']);
        this.form.get('components').get('navbarSecondary').get('backgroundColor').patchValue(material['100']);
        this.form.get('components').get('navbarPrimary').get('backgroundColor').patchValue(material['500']);
          // this.form.patchValue({ darker: this.material['800'] });
        // }
      });

    // this.service.$palette
    //   .subscribe(x => {
    //     if (x) {
    //       this.form.patchValue(x);
    //     }
    //   });
  }
  ngAfterViewInit(){
    this.editor = new Quill.default(this.editorElementRef.nativeElement, {
      modules: { 
        toolbar: this.toolbarOptions
      },
      theme: 'snow',
    });
    // console.log(this.editor);
    setTimeout(() => {
      // this.editor?.setText(this.form.get('footerText').value);
      this.editor?.pasteHTML(this.form.get('footerText').value);
      const itemToScrollTo = document.getElementById('theme-config');
      itemToScrollTo.scrollIntoView();
    },500)

    this.editor?.on('text-change', (e) => {
      this.form.markAsDirty();
      // console.log(this.editor.root);
      // console.log(this.editor.editor.delta.ops[0].insert);
    });
    
    this.dialogData.initialData.pipe(
      // first(x => !!x && x.themeName !== PWC_THEME.themeName),
    ).subscribe(value => {
      // console.log(value);
      if(this.editor){
        this.editor.root.innerHTML = value.footerText;
      }
    })
  }

  persist() {
    this.form.get('footerText').setValue(this.editor.root.innerHTML);
    this.form.markAsPristine();
    this.dialogData.onThemeChange(this.form.value);
    this.dialogData.onPersist(this.form.value);
    this._dialogRef.close();
    
  }

  cancel() {
    // this.form.reset(this.dialogData.initialData);
    this._dialogRef.close();
    }

  themeSelectionChange(tema) {
    tema = {...this.form.value, ...tema};
    this.form.patchValue(tema, { emitEvent: false });
    this.form.markAsDirty();
    // this.dialogData.onThemeChange(tema);
  }

  userChangedForm(param: Partial<ThemeConfig>) {
    this.form.patchValue(param);
    this.form.markAsDirty();
  }
}
